import { addToEcommCart } from '@/actions/cartActions';
import { featureFlagNames } from '@/client/featureFlagClient';
import {
  getCartItemsFromCartItemGroups,
  getMembershipInCart,
} from '@/components/Cart/cart.utils';
import { useAnalytics } from '@/hooks/analytics';
import { GIFT } from '@/lib/constants/memberships';
import { getIsValidGiftMembership } from '@/lib/utils/cartManager';
import {
  getFamilyCTADisplayData,
  getHasFamilyMemberships,
} from '@/lib/utils/familyHelpers';
import { useSiteStore } from '@/store/SiteStoreProvider';
import { useMemo } from 'react';
import { Product } from 'services/generated/commerce-service/models/Product';
import { ANNUAL_MEMBERSHIP_SKU } from 'ui/utils/constants/membershipConstants';
import { AmplitudeEvents } from 'whoop-analytics/lib/types';
import { useShallow } from 'zustand/react/shallow';
import DefaultBandSelector from './DefaultBandSelector/DefaultBandSelector';
import { Variant } from 'services/generated/commerce-service/models/Variant';
import CTASection from './CTASection/CTASection';
import EngravingSection from '../EngravingSection/EngravingSection';
import { getFirstVariant } from 'ui';
import { getPriceValues } from 'ui/utils/variantHelpers';
import { getFormatters } from 'ui/hooks/formatter';
import FilteredBandSelector from './FilteredBandSelector/FilteredBandSelector';
import { JF_BAND_SELECTOR } from '@/lib/constants/experiments';
import { formattedNameAndPriceForVariant } from '@/lib/utils/productHelpers';

interface IBandSelectorProps {
  onSwatchClick: (variant: Variant) => void;
  onSwatchHover: (variant?: Variant) => void;
  isMobile?: boolean;
  swatchProducts: Product[];
  selectedVariant: Variant;
  hoveredVariant?: Variant;
  skipMemSelection: string | undefined;
  handleJoinNow: (destination: string, continueWithoutBundle?: boolean) => void;
  isLoading: boolean;
  showEngravingModal: boolean;
  setShowEngravingModal: (showEngravingModal: boolean) => void;
  checkSkipMembership: () => Promise<void>;
  setIsLoading: (isLoading: boolean) => void;
  handleContinue: (
    destination: string,
    continueWithoutBundle?: boolean,
  ) => void;
}

const BandSelector: React.FC<IBandSelectorProps> = ({
  onSwatchClick,
  onSwatchHover,
  isMobile,
  selectedVariant,
  skipMemSelection,
  handleJoinNow,
  isLoading,
  setIsLoading,
  handleContinue,
  setShowEngravingModal,
  checkSkipMembership,
  hoveredVariant,
  showEngravingModal,
  swatchProducts,
}) => {
  const {
    currency,
    region,
    ecommCatalog,
    isRafGiftFlow,
    country,
    cart,
    setEcommCart,
    featureFlags,
    setAsAGift,
    language,
    experimentVariants,
  } = useSiteStore(
    useShallow((state) => ({
      currency: state.currency,
      region: state.region,
      ecommCatalog: state.ecommCatalog,
      isRafGiftFlow: state.isRafGiftFlow,
      country: state.country,
      cart: state.cart,
      setEcommCart: state.setEcommCart,
      featureFlags: state.featureFlags,
      setAsAGift: state.setAsAGift,
      language: state.language,
      experimentVariants: state.experimentVariants,
    })),
  );

  const { trackAmplitudeEvent } = useAnalytics();
  const { formatDisplayPrice } = getFormatters({ currency, language, region });
  const skipGiftSelection = skipMemSelection === GIFT;

  const engravingProduct: Product | null =
    ecommCatalog.initial_products.find(
      (product) => product.product_type === 'engraving',
    ) ?? null;

  const israelDisabled = featureFlags[featureFlagNames.DISABLE_IL_JF];
  const inIsraelAndDisabled = region === 'IL' && israelDisabled;
  const isJapanDisabled = featureFlags[featureFlagNames.DISABLE_JP_JF];
  const inJapanAndDisabled = region === 'JP' && isJapanDisabled;
  const isDisabledDueToShippingCountry =
    inJapanAndDisabled || inIsraelAndDisabled;

  const showEngravingSection =
    engravingProduct &&
    (!skipMemSelection || skipGiftSelection) &&
    !isDisabledDueToShippingCountry &&
    !getHasFamilyMemberships(
      getCartItemsFromCartItemGroups(cart.cartItemGroups),
    );

  const { familyDisplayPrice, displayFamilyMembershipCTA } = useMemo(() => {
    return getFamilyCTADisplayData({
      memberships: ecommCatalog.memberships,
      isRafGiftFlow,
      skipMemSelection,
    });
  }, [isRafGiftFlow, ecommCatalog.memberships, skipMemSelection]);

  const onFamilyCTACardClick = () => {
    trackAmplitudeEvent(AmplitudeEvents.ClickedFamilyMembership, {
      label: 'customize',
    });
    handleJoinNow('family');
  };

  const handleGift = async (): Promise<void> => {
    setIsLoading(true);
    setAsAGift(true);
    const membershipInCart = getMembershipInCart(cart.cartItemGroups);
    const hasGiftMembershipInCart = getIsValidGiftMembership(membershipInCart);
    if (!hasGiftMembershipInCart && cart.id) {
      const newCart = await addToEcommCart({
        requestBody: {
          cart_id: cart.id,
          country,
          locale: language,
          sku: ANNUAL_MEMBERSHIP_SKU,
          quantity: 1,
        },
      });
      setEcommCart(newCart);
    }
    await handleContinue('membership');
  };

  const isBandSelectorExperiment = Boolean(
    experimentVariants?.[JF_BAND_SELECTOR] &&
      experimentVariants?.[JF_BAND_SELECTOR] !== 'control',
  );

  return (
    <>
      {isBandSelectorExperiment ? (
        <>
          <FilteredBandSelector
            products={ecommCatalog.initial_products}
            isMobile={isMobile}
            onSwatchClick={onSwatchClick}
            onSwatchHover={onSwatchHover}
            selectedVariant={selectedVariant}
            variantLabel={() =>
              formattedNameAndPriceForVariant(
                swatchProducts[0].variants[0],
                hoveredVariant ?? selectedVariant,
                currency,
              )
            }
          />
        </>
      ) : (
        <DefaultBandSelector
          isMobile={isMobile}
          language={language}
          onSwatchClick={onSwatchClick}
          onSwatchHover={onSwatchHover}
          selectedVariant={selectedVariant}
          swatchProducts={swatchProducts}
          showEngravingModal={showEngravingModal}
          variantLabel={() =>
            formattedNameAndPriceForVariant(
              swatchProducts[0].variants[0],
              hoveredVariant ?? selectedVariant,
              currency,
            )
          }
        />
      )}
      {/* Engraving section */}
      {showEngravingSection ? (
        <EngravingSection
          cartItems={getCartItemsFromCartItemGroups(cart.cartItemGroups)}
          engravingPrice={formatDisplayPrice(
            getPriceValues(getFirstVariant(engravingProduct)).displayPrice,
          )}
          showEngraving={() => setShowEngravingModal(true)}
        />
      ) : null}
      <CTASection
        checkSkipMembership={checkSkipMembership}
        displayFamilyMembershipCTA={displayFamilyMembershipCTA}
        familyDisplayPrice={familyDisplayPrice}
        handleGift={handleGift}
        handleJoinNow={handleJoinNow}
        isDisabled={isDisabledDueToShippingCountry}
        isLoading={isLoading}
        onFamilyCTACardClick={onFamilyCTACardClick}
        skipGiftSelection={skipGiftSelection}
        skipMemSelection={skipMemSelection}
      />
    </>
  );
};

export default BandSelector;
